'use client';
import { useQuery } from '@tanstack/react-query';
import { createClient } from '@/lib/supabase/client';
import { getAuthUserWithRetry } from '@/lib/supabase/auth-user';
import { lookupUserRecordByIdentityDetailed } from '@/lib/supabase/user-identity';
import { useAuthStore } from '@/stores/auth-store';

/** Discover Employee choice only for the caller's active casual employment. */
export function useCasualWorkerNavigation(enabled: boolean) {
  const user = useAuthStore((state) => state.user);
  const result = useQuery({
    queryKey: ['my-casual-navigation', user?.tenantId, user?.id],
    enabled: enabled && !!user?.tenantId && !!user?.id,
    staleTime: 60_000,
    queryFn: async () => {
      if (!user?.tenantId || !user.id) return false;
      const supabase = createClient();
      const { data: auth, error: authError } = await getAuthUserWithRetry(supabase);
      if (authError || !auth.user || auth.user.id !== user.id) return false;
      // Same session-client identity lookup as the auth-profile resolver. Staff
      // references public.users.id, which can differ from the Auth principal.
      const { record: profile, failed } = await lookupUserRecordByIdentityDetailed<{
        id: string;
        tenant_id: string | null;
        is_active: boolean | null;
        status: string | null;
      }>(supabase, auth.user, 'id, tenant_id, is_active, status');
      if (failed) throw new Error('Employment type could not be checked.');
      if (
        !profile?.id ||
        profile.tenant_id !== user.tenantId ||
        profile.is_active === false ||
        profile.status?.toUpperCase() === 'INACTIVE'
      )
        return false;
      const { data, error } = await supabase
        .from('staff')
        .select('employment_type')
        .eq('tenant_id', user.tenantId)
        .eq('user_id', profile.id)
        .eq('is_active', true)
        .maybeSingle();
      if (error) throw new Error('Employment type could not be checked.');
      return data?.employment_type === 'CASUAL';
    },
  });
  return !result.isError && result.data === true;
}
